import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';

import { ChangeDetectorRef, Component, DestroyRef, ElementRef, Inject, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from "@angular/router";
import { FormsModule } from '@angular/forms';
import { EditorModule } from 'primeng/editor';

import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSidenav } from '@angular/material/sidenav';

import { HttpErrorResponse } from '@angular/common/http';

import { combineLatest, EMPTY, filter, firstValueFrom, switchMap, take } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';
import { ViewEntity } from '../../objects/view-entity';
import { EntityType } from '../../objects/interface-library';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';
import { Party } from '../../objects/party';
import { TimecardSpec, ContractInfo } from '../../objects/timecard-spec';
import { TimecardsService } from '../../service/entity-services/timecards.service';
import { normalizeEmail } from '../../shared/utils/normalize-email';
import { ContractSelectionDialogComponent, ContractSelectionDialogData } from '../../shared/components/contract-selection-dialog/contract-selection-dialog.component';
import { environment } from '../../../environments/environment';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { SignInEmailDialogComponent, SignInEmailDialogData } from '../../shared/components/sign-in-email-dialog/sign-in-email-dialog.component';
import { AttachmentConnectionsComponent } from '../../entity-form-components/attachment-connections/attachment-connections.component';
import { BaaConnectionsComponent } from '../../entity-form-components/baa-connections/baa-connections.component';
import { CoiConnectionsComponent } from '../../entity-form-components/coi-connections/coi-connections.component';
import { ContractConnectionsComponent } from '../../entity-form-components/contract-connections/contract-connections.component';
import { CriticalDateConnectionsComponent } from '../../entity-form-components/critical-date-connections/critical-date-connections.component';
import { EntityActionsSidenavComponent, EntityAction, EntityTab } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { NotesComponent } from '../../entity-form-components/notes/notes.component';
import { NoteRecord } from '../../objects/note-record';
import { accountingName } from '../../shared/utils/accounting-name';
import { AssessmentConnectionsComponent } from '../../entity-form-components/assessment-connections/assessment-connections.component';

@Component({
    selector: 'app-vendor',
    templateUrl: './vendor.component.html',
    styleUrls: ['./vendor.component.css'],
    standalone: true,
    imports: [
      CommonModule,
      MatTabsModule,
      AssessmentConnectionsComponent,
      FormsModule,
      EditorModule,
      MatSidenavModule, // This single module imports MatSidenavContainer, MatSidenav, and MatSidenavContent
      MatGridListModule,
      MatFormFieldModule,
      MatButtonModule,
      MatButtonToggleModule,
      MatCardModule,
      MatProgressSpinner,
      MatDivider,
      MatIconModule,
      MatExpansionModule,
      MatSlideToggleModule,
      MatSnackBarModule,
      MatProgressSpinnerModule,
      MatTooltipModule,
      AttachmentConnectionsComponent,
      BaaConnectionsComponent,
      CoiConnectionsComponent,
      ContractConnectionsComponent,
      CriticalDateConnectionsComponent,
      EntityActionsSidenavComponent,
      NotesComponent
    ]
})
export class VendorComponent extends ViewEntity implements OnInit {
  // attachmentsService and contractService are inherited from ViewEntity/Entity

  /**
   * Accounting Company Name for the template: the entered value if there is one,
   * else NetSuite's print-on-check-as. See `accountingName` for why that field.
   */
  get accountingCompanyName(): string {
    return accountingName(this.currentVendor);
  }

  currentVendor!: Party | null;
  partyContracts: string[] = [];
  partyBaas: string[] = [];
  partyCois: string[] = [];

  selectedIndex = 5; // Starts on the Details tab

  riskAndAssessmentObjectArray = [];
  myAssessmentsObjectArray = [];
  riskAndAssessmentTabOnScreen = true;

  submitted = false;
  reviewer = true;

  addPerson = false;
  waitingForServer = false;

  stringParamVendorNumber: string | null = null;

  parentVendorFriendlyName: string | null = null;
  parentParty: Party | null = null;
  childParties: Party[] = [];

  // Timecards tab state
  private timecardsService = inject(TimecardsService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  linkedTimecardSpec: TimecardSpec | null = null;
  timecardCreating = false;

  resizeAttachmentGrid: boolean = false;
  resizeDateGrid: boolean = false;

  /** True iff this party is a child party (has a parentVendor reference). */
  get isChildParty(): boolean {
    return !!this.currentVendor?.parentVendor;
  }

  /**
   * Friendly names of the identity fields that block enabling timecards.
   * Email is deliberately NOT one of them: NetSuite parties are read-only and
   * mostly have no email, so a missing email is asked for at enable time and
   * stored on the spec instead (see SignInEmailDialogComponent).
   * The Timecards tab uses this to render an inline banner explaining
   * exactly what to fill in before the toggle becomes interactive.
   */
  get missingTimecardsFields(): string[] {
    const v = this.currentVendor;
    const missing: string[] = [];
    if (!v?.firstName?.trim()) missing.push('First name');
    if (!v?.lastName?.trim()) missing.push('Last name');
    return missing;
  }

  /** Sign-in email typed into SignInEmailDialog when the party has none on file. */
  private timecardSignInEmail = '';

  /**
   * The email used for downstream provisioning (the timecards backend uses it to
   * provision the HCP's Cognito login). Canonical `email` first, then the NetSuite
   * alternative — `alternativeEmail` (where the import maps `paymentNotificationEmail`)
   * or the raw `paymentNotificationEmail`. NetSuite's standard email is only ~2.6%
   * populated; real addresses usually sit in the alternative, so this fallback lets
   * far more imported HCPs enable timecards without a manual email re-entry.
   */
  get effectiveEmail(): string {
    const v = this.currentVendor;
    return (
      v?.email?.trim() ||
      v?.alternativeEmail?.trim() ||
      v?.paymentNotificationEmail?.trim() ||
      ''
    );
  }

  /**
   * True iff the toggle to enable timecards should be interactive: the party
   * is a child party AND has all three identity fields. Read by the template
   * to bind the toggle's [disabled] state and to gate `enableTimecards()`.
   */
  get canEnableTimecards(): boolean {
    return this.isChildParty && this.missingTimecardsFields.length === 0;
  }

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Edit',
      icon: 'edit',
      action: () => this.editParty(),
      disabled: this.userRole === 'Viewer'
    },
    {
      label: 'Add Attachment',
      icon: 'attach_file',
      action: () => this.addAttachmentToComponent(),
      disabled: false
    },
    {
      label: 'New Important Date',
      icon: 'event',
      action: () => this.addDateToComponent(),
      disabled: false
    }
  ]);

  // Tabs configuration using signals
  sidenavTabs = signal<EntityTab[]>([
    { label: 'Contracts', index: 0, icon: 'description' },
    { label: 'Parties', index: 1, icon: 'business' },
    { label: 'People', index: 2, icon: 'people' },
    { label: 'BAAs', index: 3, icon: 'policy' },
    { label: 'COIs', index: 4, icon: 'verified_user' },
    { label: 'Details', index: 5, icon: 'info' },
    { label: 'Description', index: 6, icon: 'article' },
    { label: 'Attachments', index: 7, icon: 'attach_file' },
    { label: 'NMC', index: 8, icon: 'medical_services' },
    { label: 'Assessments', index: 9, icon: 'assessment' },
    { label: 'Dates', index: 10, icon: 'event' },
    { label: 'Notes', index: 11, icon: 'note' },
    { label: 'Timecards', index: 12, icon: 'schedule' }
  ]);

  constructor(public router: Router,
              public snackBar: MatSnackBar,
              private route: ActivatedRoute,
              public dialog: MatDialog) {

      super();

  }

  ngOnInit() {
    this.titleService.setTitle('Party');

    // Load on every :id change, not just the first. Angular reuses this component
    // for /vendor/A → /vendor/B (the parent/child party links), so loading only in
    // ngOnInit left the previous party on screen.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = params.get('id');
      if (id === this.stringParamVendorNumber && this.currentVendor) return;
      this.stringParamVendorNumber = id;
      this.loadParty();
    });
  }

  private async loadParty() {
    this.currentVendor = null;
    this.entityAttachments = [];
    this.entityDates = [];
    this.cdr.markForCheck();

    if (this.stringParamVendorNumber) {
      const requested = this.stringParamVendorNumber;
      const party = await firstValueFrom(this.partyService.getPartyObjectFullyHydrated(requested));
      // A newer navigation started while this one was loading — let it win.
      if (requested !== this.stringParamVendorNumber) return;
      this.currentVendor = party;
      this.cdr.markForCheck();   // zoneless: assignment after an await needs an explicit notify

      // console.log('Full Party Object:', JSON.stringify(this.currentVendor, null, 2));

      // NetSuite-sourced parties are read-only in HC: disable the "Edit" action so the
      // detail view matches the disabled edit form. HC-additive actions (attachments,
      // important dates) stay enabled — they don't touch NetSuite-owned fields.
      if (this.currentVendor?.sourceSystem === 'netsuite') {
        this.sidenavActions.update(actions =>
          actions.map(a => a.label === 'Edit' ? { ...a, disabled: true } : a)
        );
      }

    } else {
      console.error('vendor | ngOnInit | No vendor number found.');
    }

    // GET THE ATTACHMENTS ASSOCIATED WITH THIS PARTY
    // NEW API (Phase 1): Query by parentEntityId, with fallback to legacy attachment IDs array
    if (this.currentVendor?.vendorNum) {
      this.attachmentsService.getAttachmentsForEntity(this.currentVendor.vendorNum, 'Party')
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentVendor?.attachments?.length) {
            // Fallback: ETL'd parties may not have parentEntityId on attachments
            this.attachmentsService.getAttachmentsByNumbers(this.currentVendor.attachments)
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
              });
          } else {
            this.entityAttachments = [];
          }
        });
    } else {
      this.entityAttachments = [];
    }


    // GET THE DATES ASSOCIATED WITH THIS PARTY
    this.criticalDatesService.getDatesByNumbers(this.currentVendor?.criticalDates)
      .subscribe(dates => {
        this.entityDates = dates;
        this.cdr.markForCheck();   // zoneless: post-HTTP assignment needs an explicit notify
        // console.log('*** Party dates array: ', this.entityDates);
      });

    // LOAD ASSOCIATED ENTITIES FOR THIS PARTY
    this.loadPartyContracts();
    this.loadPartyBaas();
    this.loadPartyCois();

    // LOAD PARENT/CHILD PARTY RELATIONSHIPS
    await this.loadPartyRelationships();
  }

  /**
   * Loads parent and child party relationships for the current party
   */
  private async loadPartyRelationships(): Promise<void> {
    if (!this.currentVendor) return;

    // settledCache, NOT firstValueFrom: this drives BOTH the parent lookup and the
    // child-party roster below. Against a partial cache the parent silently
    // resolves to null and the child list is whatever subset of children happened
    // to be in the first page — so a NetSuite agency's HCPs mostly vanished from
    // its detail view. `filter` over an incomplete list under-reports with no error.
    const allParties = await settledCache(this.partyService.allParties$);

    // If this party has a parent, find it
    if (this.currentVendor.parentVendor) {
      this.parentParty = allParties.find(p => p.vendorNum === this.currentVendor?.parentVendor) || null;
      this.parentVendorFriendlyName = this.parentParty?.name || null;
    }

    // Find all child parties (parties that have this party as their parent)
    this.childParties = allParties.filter(p => p.parentVendor === this.currentVendor?.vendorNum);
    // console.log('Child parties found:', this.childParties.map(c => ({ name: c.name, vendorNum: c.vendorNum, parentVendor: c.parentVendor })));
  }




  /**
   * Loads contracts associated with this party by scanning the contracts cache.
   * Finds contracts where this party is the directVendor or in otherParties.
   */
  private loadPartyContracts(): void {
    if (!this.currentVendor?.vendorNum) return;

    const vendorNum = this.currentVendor.vendorNum;

    // Load contracts in combination with BAAs and COIs so we can find
    // contracts associated indirectly via BAA/COI relationships
    combineLatest([
      this.contractService.allContracts$.pipe(take(1)),
      this.baaService.allBaas$.pipe(take(1)),
      this.coiService.allCois$.pipe(take(1))
    ]).subscribe(([allContracts, allBaas, allCois]) => {
      const matchingContractIds = new Set<string>();

      // Add any contract IDs from the party's own contracts field (set by relationship API)
      if (this.currentVendor?.contracts && Array.isArray(this.currentVendor.contracts)) {
        this.currentVendor.contracts.forEach((c: any) => {
          const id = typeof c === 'string' ? c : c?.contractId || c?.contractNum;
          if (id) matchingContractIds.add(id);
        });
      }

      // Build a set of BAA nums/IDs where this party is referenced
      const partyBaaNums = new Set<string>();
      allBaas.forEach(baa => {
        const baaNum = baa.baaNum;
        if (!baaNum) return;
        const partyFields = [baa.party, baa.coveredEntity, baa.businessAssociateName];
        for (const field of partyFields) {
          const fieldNum = typeof field === 'string' ? field : field?.vendorNum;
          if (fieldNum === vendorNum) {
            partyBaaNums.add(baaNum);
            if (baa.baaId) partyBaaNums.add(baa.baaId);
            break;
          }
        }
      });

      // Build a set of COI nums/IDs where this party is referenced
      const partyCoiNums = new Set<string>();
      allCois.forEach(coi => {
        const coiNum = coi.coiNum;
        if (!coiNum) return;
        let matched = false;
        const singleFields = [coi.insuredParty, coi.partyProvidingCoi];
        for (const field of singleFields) {
          const fieldNum = typeof field === 'string' ? field : field?.vendorNum;
          if (fieldNum === vendorNum) {
            partyCoiNums.add(coiNum);
            if (coi.coiId) partyCoiNums.add(coi.coiId);
            matched = true;
            break;
          }
        }
        // insuranceProvider is an array
        if (!matched && Array.isArray(coi.insuranceProvider)) {
          for (const provider of coi.insuranceProvider) {
            const provNum = typeof provider === 'string' ? provider : provider?.vendorNum;
            if (provNum === vendorNum) {
              partyCoiNums.add(coiNum);
              if (coi.coiId) partyCoiNums.add(coi.coiId);
              break;
            }
          }
        }
      });

      // Scan contracts for direct party references AND indirect BAA/COI references
      allContracts.forEach(contract => {
        const contractId = contract.contractId || contract.contractNum;
        if (!contractId) return;

        // Check directVendor
        const dv = contract.directVendor;
        const dvNum = typeof dv === 'string' ? dv : dv?.vendorNum;
        if (dvNum === vendorNum) {
          matchingContractIds.add(contractId);
        }

        // Check otherParties
        if (Array.isArray(contract.otherParties)) {
          contract.otherParties.forEach((p: any) => {
            const pNum = typeof p === 'string' ? p : p?.vendorNum;
            if (pNum === vendorNum) {
              matchingContractIds.add(contractId);
            }
          });
        }

        // Check if contract references a BAA that involves this party
        if (contract.directBaa) {
          const baaRef = typeof contract.directBaa === 'string' ? contract.directBaa : (contract.directBaa as any)?.baaNum || (contract.directBaa as any)?.baaId;
          if (baaRef && partyBaaNums.has(baaRef)) {
            matchingContractIds.add(contractId);
          }
        }
        if (Array.isArray(contract.baas)) {
          contract.baas.forEach((b: any) => {
            const bRef = typeof b === 'string' ? b : b?.baaNum || b?.baaId;
            if (bRef && partyBaaNums.has(bRef)) {
              matchingContractIds.add(contractId);
            }
          });
        }

        // Check if contract references a COI that involves this party
        if (contract.directCoi) {
          const coiRef = typeof contract.directCoi === 'string' ? contract.directCoi : (contract.directCoi as any)?.coiNum || (contract.directCoi as any)?.coiId;
          if (coiRef && partyCoiNums.has(coiRef)) {
            matchingContractIds.add(contractId);
          }
        }
        if (Array.isArray(contract.cois)) {
          contract.cois.forEach((c: any) => {
            const cRef = typeof c === 'string' ? c : c?.coiNum || c?.coiId;
            if (cRef && partyCoiNums.has(cRef)) {
              matchingContractIds.add(contractId);
            }
          });
        }
      });

      this.partyContracts = Array.from(matchingContractIds);
      // console.log(`[Party] Found ${this.partyContracts.length} associated contracts for vendor ${vendorNum}`);
    });
  }

  /**
   * Loads BAAs associated with this party by checking the party's baas field
   * and scanning the BAA cache for BAAs referencing this party.
   */
  private loadPartyBaas(): void {
    if (!this.currentVendor?.vendorNum) return;

    const vendorNum = this.currentVendor.vendorNum;

    console.log('[Vendor:BAA-Assoc] Starting BAA scan for vendorNum:', vendorNum);
    this.baaService.allBaas$.pipe(take(1)).subscribe(allBaas => {
      console.log('[Vendor:BAA-Assoc] BAA cache size:', allBaas.length);
      const matchingBaaIds = new Set<string>();

      // Add BAA IDs from the party's own baas field (set by relationship API)
      if (this.currentVendor?.baas && Array.isArray(this.currentVendor.baas)) {
        console.log('[Vendor:BAA-Assoc] Party has baas field with', this.currentVendor.baas.length, 'entries');
        this.currentVendor.baas.forEach((b: any) => {
          const id = typeof b === 'string' ? b : b?.baaNum;
          if (id) matchingBaaIds.add(id);
        });
      }

      // Scan the BAA cache for BAAs referencing this party
      allBaas.forEach(baa => {
        const baaId = baa.baaNum;
        if (!baaId) return;

        // Check party, coveredEntity, businessAssociateName fields
        const partyFields = [baa.party, baa.coveredEntity, baa.businessAssociateName];
        for (const field of partyFields) {
          const fieldNum = typeof field === 'string' ? field : field?.vendorNum;
          if (fieldNum === vendorNum) {
            console.log('[Vendor:BAA-Assoc] Match! BAA', baaId, 'references this party via field value:', fieldNum);
            matchingBaaIds.add(baaId);
            break;
          }
        }
      });

      this.partyBaas = Array.from(matchingBaaIds);
      console.log('[Vendor:BAA-Assoc] Result:', this.partyBaas.length, 'BAAs found. IDs:', this.partyBaas);
    });
  }

  /**
   * Loads COIs associated with this party by checking the party's cois field
   * and scanning the COI cache for COIs referencing this party.
   */
  private loadPartyCois(): void {
    if (!this.currentVendor?.vendorNum) return;

    const vendorNum = this.currentVendor.vendorNum;

    console.log('[Vendor:COI-Assoc] Starting COI scan for vendorNum:', vendorNum);
    this.coiService.allCois$.pipe(take(1)).subscribe(allCois => {
      console.log('[Vendor:COI-Assoc] COI cache size:', allCois.length);
      const matchingCoiIds = new Set<string>();

      // Add COI IDs from the party's own cois field (set by relationship API)
      if (this.currentVendor?.cois && Array.isArray(this.currentVendor.cois)) {
        console.log('[Vendor:COI-Assoc] Party has cois field with', this.currentVendor.cois.length, 'entries');
        this.currentVendor.cois.forEach((c: any) => {
          const id = typeof c === 'string' ? c : c?.coiNum;
          if (id) matchingCoiIds.add(id);
        });
      }

      // Scan the COI cache for COIs referencing this party
      allCois.forEach(coi => {
        const coiId = coi.coiNum;
        if (!coiId) return;

        // Check insuredParty, partyProvidingCoi, insuranceProvider fields
        const singleFields = [coi.insuredParty, coi.partyProvidingCoi];
        let matched = false;
        for (const field of singleFields) {
          const fieldNum = typeof field === 'string' ? field : field?.vendorNum;
          if (fieldNum === vendorNum) {
            console.log('[Vendor:COI-Assoc] Match! COI', coiId, 'references this party via field value:', fieldNum);
            matchingCoiIds.add(coiId);
            matched = true;
            break;
          }
        }
        // insuranceProvider is an array
        if (!matched && Array.isArray(coi.insuranceProvider)) {
          for (const provider of coi.insuranceProvider) {
            const provNum = typeof provider === 'string' ? provider : provider?.vendorNum;
            if (provNum === vendorNum) {
              console.log('[Vendor:COI-Assoc] Match! COI', coiId, 'references this party via insuranceProvider:', provNum);
              matchingCoiIds.add(coiId);
              break;
            }
          }
        }
      });

      this.partyCois = Array.from(matchingCoiIds);
      console.log('[Vendor:COI-Assoc] Result:', this.partyCois.length, 'COIs found. IDs:', this.partyCois);
    });
  }

  addAttachmentToComponent(): void {
    const entityObjectForDialog = {
      ...this.currentVendor,
      entityType: EntityType.Party
    };

    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityObject: entityObjectForDialog
      }
    });

    dialogRef.afterClosed().subscribe(newAttachment => {
      if (!this.currentVendor) return;

      if (newAttachment && newAttachment.attachmentNum) {
        if (this.currentVendor.vendorNum) {
          this.attachmentsService.getAttachmentsForEntity(this.currentVendor.vendorNum, 'Party')
            .pipe(take(1))
            .subscribe({
              next: (attachments) => {
                this.entityAttachments = attachments;
              },
              error: (err) => {
                console.error('Failed to fetch attachments:', err);
                this.entityAttachments = [...this.entityAttachments, newAttachment];
              }
            });
        }
      }
    });
  }

  addDateToComponent(): void {
    // Use the base class method from ViewEntity
    this.addCriticalDate(
      this.currentVendor,
      'vendorNum',
      (id, update) => this.partyService.updatePartyPartially(id, update)
    );
  }




    selectTab(index: number) {
      this.selectedIndex = index;

      if (index === 7) { // Attachments tab (was 3)
        setTimeout(() => {
          this.resizeAttachmentGrid = true;
        }, 100);
      }

      if (index === 10) { // Dates tab (was 6)
        setTimeout(() => {
          this.resizeDateGrid = true;
        }, 100);
      }
    }
    

    onGridResized(): void {
      this.resizeAttachmentGrid = false;
      this.resizeDateGrid = false;
    }  


  snackBarMessage(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 2000,
    });
  }

  editParty(): void {
    // Read-only for NetSuite-sourced parties; the Edit action is disabled, this is
    // defense-in-depth (and the edit form hard-blocks save too).
    if (this.currentVendor?.sourceSystem === 'netsuite') {
      return;
    }
    if (this.currentVendor?.vendorNum) {
      this.router.navigate(['/securehome/new-vendor-full-page', this.currentVendor.vendorNum]);
    }
  }

  navigateToParty(vendorNum: string | null | undefined): void {
    if (vendorNum) {
      this.router.navigate(['/securehome/vendor', vendorNum]);
    }
  }

  backToPartyList(): void {
    this.router.navigate(['/securehome/parties']);
  }

  /**
   * Extract display name from a user field (could be hydrated object or email string)
   */
  getOwnerDisplayName(owner: any): string {
    if (!owner) return 'Not Assigned';

    if (typeof owner === 'object' && owner.userData) {
      const { given_name, family_name, email } = owner.userData;
      if (given_name && family_name) {
        return `${family_name}, ${given_name}`;
      }
      return email || owner.userId || 'Unknown User';
    }

    if (typeof owner === 'string') {
      return this.usersService.getUserDisplayNameByEmail(owner);
    }

    if (typeof owner === 'object' && owner.friendlyName) {
      return owner.friendlyName;
    }

    if (typeof owner === 'object' && owner.shortFriendlyName) {
      return owner.shortFriendlyName;
    }

    if (typeof owner === 'object' && owner.email) {
      return owner.email;
    }

    return 'Unknown User';
  }

  refreshAttachments(): void {
    if (this.currentVendor?.vendorNum) {
      this.attachmentsService.getAttachmentsForEntity(this.currentVendor.vendorNum, 'Party')
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentVendor?.attachments?.length) {
            this.attachmentsService.getAttachmentsByNumbers(this.currentVendor.attachments)
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
              });
          } else {
            this.entityAttachments = [];
          }
        });
    }
  }

  refreshCriticalDates(): void {
    if (this.currentVendor?.criticalDates && this.currentVendor.criticalDates.length > 0) {
      this.criticalDatesService.getDatesByNumbers(this.currentVendor.criticalDates)
        .subscribe(dates => {
          // console.log('*** Refreshed critical dates:', dates);
          this.entityDates = dates;
          this.cdr.markForCheck();
        });
    }
  }

  saveNotes(notes: NoteRecord[]): void {
    if (!this.currentVendor) return;
    this.currentVendor.notes = notes;
    this.partyService.writeParty(this.currentVendor)
      .subscribe({
        error: (err) => console.error('Failed to save notes:', err)
      });
  }

  // ── Timecards ──────────────────────────────────────────

  /** Show confirmation dialog and create a timecard spec for this child party. */
  enableTimecards(): void {
    // Defense in depth: the toggle is also visually disabled when this fails,
    // but a stale UI binding shouldn't be able to fire a doomed write.
    if (!this.currentVendor || !this.canEnableTimecards) return;

    const party = this.currentVendor;
    const displayName = party.name || `${party.firstName ?? ''} ${party.lastName ?? ''}`.trim() || 'this HCP';

    // No email on the party (typical for NetSuite imports, which are read-only):
    // ask for the sign-in email first. It goes on the spec, not the party.
    this.timecardSignInEmail = '';
    if (!this.effectiveEmail) {
      this.dialog.open<SignInEmailDialogComponent, SignInEmailDialogData, string | null>(SignInEmailDialogComponent, {
        data: { partyName: displayName, feature: 'timecards' },
        width: '480px'
      }).afterClosed().subscribe(email => {
        if (!email) return;
        this.timecardSignInEmail = email;
        this.confirmEnableTimecards(displayName);
      });
      return;
    }
    this.confirmEnableTimecards(displayName);
  }

  private confirmEnableTimecards(displayName: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Enable Timecards',
        message: `This will create a timecard specification for "${displayName}". This links the party to the timecards system and cannot be undone. Do you want to continue?`,
        confirmLabel: 'Yes, Enable Timecards',
        cancelLabel: 'Cancel',
        icon: 'schedule',
        warn: false
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed || !this.currentVendor) return;

      // Step 2: contract selection — required before spec creation.
      const p = this.currentVendor;
      const displayName = p.name || `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || 'this HCP';
      const contractDialogRef = this.dialog.open<
        ContractSelectionDialogComponent,
        ContractSelectionDialogData,
        ContractInfo[] | null
      >(ContractSelectionDialogComponent, {
        data: {
          partyDisplayName: displayName,
          partyNum: p.vendorNum ?? '',
          parentVendorNum: p.parentVendor ?? null
        },
        disableClose: false
      });

      contractDialogRef.afterClosed().subscribe(selectedContracts => {
        if (!selectedContracts || selectedContracts.length === 0 || !this.currentVendor) return;
        this.createTimecardSpec(selectedContracts);
      });
    });
  }

  /** Step 2 finalize — creates the TimecardSpec with the chosen contracts attached. */
  private createTimecardSpec(contracts: ContractInfo[]): void {
    if (!this.currentVendor) return;
    this.timecardCreating = true;
    this.cdr.detectChanges();

    // Hoisted out of switchMap so the error handler can log what was sent.
    // TODO: trim debug logs once the spec-write flow is stable.
    let lastSeedSent: TimecardSpec | null = null;

    this.usersService.currentUserEmail$.pipe(
      filter((e): e is string => !!e),
      take(1),
      switchMap(_ownerEmail => {
        const p = this.currentVendor!;
        // For timecards the principal IS the HCP party, so principalEmail = the
        // party's normalized email. The backend requires it for its proxy-lookup
        // GSI and 400s without it (same contract as timesheets). Guard if absent.
        const signInEmail = this.effectiveEmail || this.timecardSignInEmail;
        const principalEmail = normalizeEmail(signInEmail);
        if (!principalEmail) {
          this.timecardCreating = false;
          this.snackBar.open(
            'No sign-in email was entered, so timecards were not enabled.',
            'Dismiss',
            { duration: 6000 }
          );
          this.cdr.detectChanges();
          return EMPTY;
        }
        const seed: TimecardSpec = {
          partyNum: p.vendorNum ?? '',
          firstName: p.firstName ?? '',
          lastName: p.lastName ?? '',
          email: signInEmail,
          principalEmail,
          credentials: [],
          positions: [],
          sites: [],
          contracts,
          specState: 'draft',
          active: true
        };
        lastSeedSent = seed;
        // Debug: paired with backend Lambda logs for diagnosing 4xx on spec-write.
        const writeUrl = (environment as any).URL_TIMECARD_SPECS_WRITE;
        console.log('[timecard-spec-write] →', writeUrl, seed);
        return this.timecardsService.writeTimecardSpec(seed);
      })
    ).pipe(take(1)).subscribe({
        next: (response: TimecardSpec) => {
          this.linkedTimecardSpec = response;
          if (this.currentVendor) {
            this.currentVendor.timecardsEnabled = true;
            this.partyService.writeParty(this.currentVendor)
              .pipe(take(1))
              .subscribe();
          }
          this.timecardCreating = false;
          this.cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          console.error('[timecard-spec-write] Failed', {
            status: err.status,
            url: err.url,
            responseBody: err.error,         // e.g. {success:false, message:"..."}
            requestPayload: lastSeedSent     // what we sent
          });
          this.timecardCreating = false;
          const detail = err?.error?.message || err?.message || 'Please try again later.';
          this.snackBar.open(`Could not enable timecards. ${detail}`, 'Dismiss', { duration: 5000 });
          this.cdr.detectChanges();
        }
      });
  }

  /** Open the linked timecard editor (route is a placeholder until phase 3). */
  editTimecardSpec(): void {
    if (this.linkedTimecardSpec?.id) {
      window.open(`/securehome/timecard-edit/${this.linkedTimecardSpec.id}`, '_blank');
    }
  }
}
